/* eslint-disable no-undef */
import React, { useState, useEffect, useCallback, useImperativeHandle, useRef } from 'react';
import { Button } from 'semantic-ui-react';
import { useLoadScript, GoogleMap, DrawingManager, Polygon, MarkerClusterer } from '@react-google-maps/api';
import _ from 'lodash';
import mapStyles from './mapStyles.json';
import { PATH_PROPERTY, PATH_TYPE, STOP_TYPE, stopIconSvg, userIconSvg } from './constants';

import { getLocationsForCustomFit, handleShape } from './helper';
import { CustomPolyLine, StopMarker, UserMarker } from './MarkersAndPolyLine';
import ActualVsPlannedMapView from './actualVsPlannedComponents/ActualVsPlannedMapView';
import styles from './map.module.css';
import Joyn2Button from '../common/Joyn2Button';
import TaskMapper from '../../HelperMethods/Mappers';
import { pointer, polygon, rectangle } from '../../common/constants';
import ApiService from '../../service/apiService';
import { setFinalCenterAndZoomInMapView, getFinalCenterAndZoomInMapView } from '../../HelperMethods/LocalStorageUtils';

/* eslint-disable no-underscore-dangle */
let selected = null;
let userMarkers = [];
let map = null;
let intervalId = null;

// Hook
function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

const useWindowUnloadEffect = (handler, callOnCleanup) => {
  const cb = useRef();

  cb.current = handler;

  useEffect(() => {
    handler = () => cb.current();

    window.addEventListener('beforeunload', handler);

    return () => {
      if (callOnCleanup) handler();

      window.removeEventListener('beforeunload', handler);
    };
  }, [callOnCleanup]);
};

const onMapLoad = (mapArg, mapTilerUrl) => {
  const layer = google
    ? new google.maps.ImageMapType({
        name: 'data-layer',
        getTileUrl: function getTileUrl(coord, zoom) {
          const TILE_URL = mapTilerUrl;
          const url = TILE_URL && TILE_URL.replace('{x}', coord.x).replace('{y}', coord.y).replace('{z}', zoom);
          return url;
        },
        tileSize: new google.maps.Size(256, 256),
        minZoom: 1,
        maxZoom: 12,
      })
    : null;
  console.info('layer', { layer });
  if (layer) {
    console.info('map options', { mapArg });
    mapArg.overlayMapTypes.insertAt(0, layer);
  }
};

const maptilerLoad = (props) => {
  if (map && props.mapTilerUrl) {
    console.info('mapTileUrl', props.mapTilerUrl);
    onMapLoad(map, props.mapTilerUrl);
  }
};

function shouldNotUpdateComponent(prevProps, nextProps) {
  const {
    markers: prevMarkers,
    paths: prevPaths,
    routeData: prevRouteData,
    showActualVsPlannedView: prevShowActualVsPlannedView,
    mapTilerUrl,
    selectedStopId,
    bulkEditSaved,
    polygons: prevPolygons,
  } = prevProps;
  const {
    markers: nextMarkers,
    paths: nextPaths,
    routeData: nextRouteData,
    showActualVsPlannedView: nextShowActualVsPlannedView,
    mapTilerUrl: nextMapTilerUrl,
    selectedStopId: prevSelectedStopId,
    bulkEditSaved: nextBulkEditSaved,
    polygons: nextPolygons,
  } = nextProps;
  if (
    !_.isEqual(prevMarkers, nextMarkers) ||
    !_.isEqual(prevPaths, nextPaths) ||
    !_.isEqual(prevPolygons, nextPolygons) ||
    prevRouteData !== nextRouteData ||
    prevShowActualVsPlannedView !== nextShowActualVsPlannedView ||
    selectedStopId !== prevSelectedStopId
  ) {
    return false;
  }
  if (bulkEditSaved !== nextBulkEditSaved) {
    return false;
  }
  if (mapTilerUrl !== nextMapTilerUrl) {
    maptilerLoad(nextProps);
    return false;
  }
  return true;
}

const WrapperMapViewPage = React.forwardRef((props, ref) => {
  const {
    handleTaskSelection,
    showActualVsPlannedView,
    bulkEditSaved,
    routeData,
    selectedFieldId,
    mapsKey,
    markers,
    isUserSelected,
  } = props;
  const prevIsUserSelected = usePrevious(isUserSelected);
  const prevSelectedFieldId = usePrevious(selectedFieldId);
  const prevMarkers = usePrevious(markers);
  const [drawingMode, changeDrawingModeState] = useState(null);
  const [showShape, changeShowShapeState] = useState(true);
  /**
   * this funciton will recenter map on locations if available, otherwise it will find out locations from data: markers and routeData
   * @param locations
   */

  const customFitToBounds = (locations) => {
    if (!map) return;
    if (!google) return;
    if (!showShape) return;
    const bounds = new google.maps.LatLngBounds();
    const locationsForCustomFit = locations || getLocationsForCustomFit(showActualVsPlannedView, markers, routeData);

    // add locations to bounds
    if (locationsForCustomFit.length > 0) {
      locationsForCustomFit.forEach((location) => {
        const loc = new google.maps.LatLng(location.lat, location.lng);
        bounds.extend(loc);
      });
    } else {
      // setting default bounds
      bounds.extend(new google.maps.LatLng(38, -119)); // somewhere near california
      bounds.extend(new google.maps.LatLng(25, 75)); // somewhere in India
    }
    // auto zoom and auto center
    map.fitBounds(bounds); // auto-zoom
    map.panToBounds(bounds); // auto-center
  };

  const handleShowShape = (show) => {
    changeShowShapeState(show);
  };

  const onMapMounted = (mapInstance) => {
    map = mapInstance;

    // when mounting map with markers, this block would be called
    if (map) {
      const centerAndZoom = getFinalCenterAndZoomInMapView();
      if (centerAndZoom && centerAndZoom.zoom > 2) {
        map.setCenter(centerAndZoom.center);
        map.setZoom(centerAndZoom.zoom);
      } else {
        console.log(' customFitToBounds onMapMounted customFitToBounds');
        customFitToBounds();
      }
    }
    maptilerLoad(props);
  };

  // const onStopsUpdate = () => {
  //   if (map) {
  //     customFitToBounds();
  //   }
  // };

  const handleDrawingMode = useCallback((drawingModeArg, show) => {
    if (!_.isNil(show)) {
      changeShowShapeState(show);
    }
    changeDrawingModeState(drawingModeArg);
  }, []);

  const onUserMarkerMounted = (prop) => {
    userMarkers = prop.getMarkers();
  };

  const handleUserLocation = useCallback(
    ({ prevUserMarker, deltaLat, deltaLng, i, callbackfunc, mapArg, userMarkersArg, updatedPosition }) => {
      const { position: prevPosition } = prevUserMarker;
      prevUserMarker.setPosition({
        lat: prevPosition.lat() + deltaLat,
        lng: prevPosition.lng() + deltaLng,
      });

      if (i !== 100) {
        i += 1;
        setTimeout(function () {
          callbackfunc({
            prevUserMarker,
            deltaLat,
            deltaLng,
            i,
            callbackfunc,
            mapArg,
            userMarkersArg,
            updatedPosition,
          });
        }, 10);
      } else if (i === 100 && updatedPosition) {
        setTimeout(function () {
          prevUserMarker.setPosition({
            lat: updatedPosition[1],
            lng: updatedPosition[0],
          });
        }, 10);
      }
    },
    []
  );

  const transition = useCallback((usersLocationInfo, markersArg) => {
    const prevuserLocationDict = _.keyBy(userMarkers, 'title');
    const updatedLocationOfMarkers = [];
    for (const userLocationInfo of usersLocationInfo) {
      const { id, position } = userLocationInfo;
      const reqUser = markersArg.find((val) => val.id === id);
      if (reqUser) {
        updatedLocationOfMarkers.push({ ...reqUser, updatedPosition: position });
      }
    }

    for (const user of updatedLocationOfMarkers) {
      const { updatedPosition } = user;
      const prevUserMarker = _.get(prevuserLocationDict, `[${user.id}]`) || {};
      if (prevUserMarker) {
        const { position: prevPosition } = prevUserMarker;
        if (prevPosition) {
          console.log('prevUserMarker', {
            id: user.id,
            name: prevUserMarker?.label?.text,
            lat: prevPosition.lat(),
            lng: prevPosition.lng(),
            newLat: updatedPosition[1],
            newLng: updatedPosition[0],
          });
          const deltaLat = (updatedPosition[1] - prevPosition.lat()) / 100;
          const deltaLng = (updatedPosition[0] - prevPosition.lng()) / 100;
          handleUserLocation({
            prevUserMarker,
            deltaLat,
            deltaLng,
            i: 0,
            callbackfunc: handleUserLocation,
            mapArg: map,
            userMarkersArg: userMarkers,
            updatedPosition,
          });
        }
      }
    }
  }, []);

  const getUsersLocationUpdate = async (selectedFieldIdArg, markersArg, loadUsersLocationsViaSubscription) => {
    try {
      const response = await ApiService.getUsersLocationByField(selectedFieldIdArg);
      const success = _.get(response, 'data.rpcService.success');
      if (success) {
        const output = _.get(response, 'data.rpcService.output');
        const usersLocationInfo = JSON.parse(JSON.parse(output));
        loadUsersLocationsViaSubscription(usersLocationInfo);
        transition(usersLocationInfo, markersArg);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const userLocationPolling = async (selectedFieldIdArg, markersArg) => {
    const { loadUsersLocationsViaSubscription } = props;
    intervalId = setInterval(
      () => getUsersLocationUpdate(selectedFieldIdArg, markersArg, loadUsersLocationsViaSubscription),
      1000 * 20
    );
  };

  const unSubscribeUserLocationUpdate = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  useImperativeHandle(ref, () => ({
    fitBound(locations) {
      customFitToBounds(locations);
    },
  }));

  const setCenterAndZoom = () => {
    if (map) {
      const { zoom = 2 } = map;
      const center = { lat: map?.center?.lat(), lng: map?.center?.lng() };
      setFinalCenterAndZoomInMapView({ zoom, center });
    }
  };

  function checkUsersmarkerEquality(markersArg, prevMarkersArg) {
    const prevMarkersDict = _.keyBy(prevMarkersArg, 'id');
    for (const marker of markersArg) {
      if (marker.type !== STOP_TYPE.USER_START_END_STOP) {
        continue;
      }
      const reqMarker = prevMarkersDict[marker.id];
      if (reqMarker) {
        const {
          location: { lat: prevLat, lng: prevLng },
        } = reqMarker;
        const {
          location: { lat, lng },
        } = marker;
        if (lat !== prevLat || lng !== prevLng) {
          return false;
        }
      } else {
        return false;
      }
    }
    return true;
  }

  const shouldRestartPolling = () => {
    if (prevSelectedFieldId !== selectedFieldId) {
      return true;
    }
    if (prevIsUserSelected || isUserSelected) {
      return false;
    }
    const isEqual = checkUsersmarkerEquality(markers, prevMarkers);
    if (isEqual) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    selected = null;
    return () => {
      console.log('map unmount');
      handleTaskSelection([]);
      if (selected) {
        google.maps.event.clearListeners(selected, 'click');
        selected.setMap(null);
        unSubscribeUserLocationUpdate();
      }
    };
  }, []);

  useEffect(() => {
    const restartPolling = shouldRestartPolling();
    if (restartPolling) {
      unSubscribeUserLocationUpdate();
      userLocationPolling(selectedFieldId, markers);
    }
  }, [selectedFieldId, markers]);

  useEffect(() => {
    if (showActualVsPlannedView) {
      console.log('customFitToBounds useEffect routeData');
      customFitToBounds();
    }
  }, [routeData]);

  useEffect(() => {
    if (bulkEditSaved && selected) {
      handleShowShape(false);
    }
  }, [bulkEditSaved]);

  useWindowUnloadEffect(setCenterAndZoom, true);

  return (
    <MapViewPage
      googleMapKey={mapsKey}
      loadingElement={<div style={{ height: `100%` }} />}
      containerElement={<div style={{ height: `100%` }} />}
      mapElement={<div style={{ height: `100%` }} />}
      onMapMounted={onMapMounted}
      onUserMarkerMounted={onUserMarkerMounted}
      drawingMode={drawingMode}
      showShape={showShape}
      handleDrawingMode={handleDrawingMode}
      {...props}
    />
  );
});

function NormalMapView(props) {
  const {
    paths: pathObjects,
    markers,
    polygons,
    routeData: { actual },
    usersSelected,
  } = props;
  const [markersOutsideClusters, setMarkersOutsideClusters] = useState([]);
  const markerLabelsHandler = useCallback((clusterer) => {
    const markersOutSideCluster = [];
    const clusters = clusterer.getClusters();
    for (let i = 0, l = clusters.length; i < l; i += 1) {
      const reqMarkers = clusters[i].getMarkers();
      for (const m of reqMarkers) {
        if (reqMarkers.length < 2) {
          const id = m.getLabel();
          if (id && id.text) {
            markersOutSideCluster.push(id.text);
          }
        }
      }
    }
    setMarkersOutsideClusters(markersOutSideCluster);
  }, []);
  const locationArr = actual?.locations?.map((r) => r.location) || [];

  return (
    <>
      {usersSelected?.length === 1 ? (
        <CustomPolyLine
          key={`${usersSelected[0]}`}
          locations={locationArr}
          strokeOpacity={PATH_PROPERTY[PATH_TYPE.RESOLVED].strokeOpacity}
          strokeWeight={PATH_PROPERTY[PATH_TYPE.RESOLVED].strokeWeight}
          strokeColor="#009574"
          icons={PATH_PROPERTY[PATH_TYPE.RESOLVED].icons}
        />
      ) : null}
      {pathObjects.map((pathObj) => {
        const locationArray = pathObj.path;
        return (
          <CustomPolyLine
            key={`${pathObj.userId}_${pathObj.pathType}`}
            locations={locationArray}
            strokeOpacity={PATH_PROPERTY[pathObj.pathType].strokeOpacity}
            strokeWeight={PATH_PROPERTY[pathObj.pathType].strokeWeight}
            strokeColor={pathObj.color}
            icons={PATH_PROPERTY[pathObj.pathType].icons}
          />
        );
      })}
      <MarkerClusterer
        onClusteringEnd={markerLabelsHandler}
        averageCenter
        enableRetinaIcons
        gridSize={60}
        styles={[
          {
            url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(stopIconSvg())}`,
            height: 45,
            width: 36,
          },
        ]}
        id="map-cluster"
      >
        {(clusterer) => {
          return markers
            .filter((m) => m.type === STOP_TYPE.TASK_STOP || m.type === STOP_TYPE.USER_START_END_STOP)
            .map((m) => (
              <StopMarker
                clusterer={clusterer}
                key={m.stopId}
                stopId={m.stopId}
                stopSequence={m.sequence}
                tasksCount={m.taskList.length}
                location={m.location}
                hideSequence={props.hideSequence}
                onStopClick={props.onStopClick}
                isStopSelected={props.selectedStopId === m.stopId}
                isMarkersOutSideCluster={markersOutsideClusters.includes(m.stopId)}
              />
            ));
        }}
      </MarkerClusterer>
      <MarkerClusterer
        onLoad={props.onUserMarkerMounted}
        averageCenter
        enableRetinaIcons
        gridSize={60}
        styles={[
          {
            url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(userIconSvg())}`,
            height: 38,
            width: 32,
          },
        ]}
        id="map-cluster"
      >
        {(clusterer) => {
          return markers
            .filter((m) => m.type === STOP_TYPE.USER_CURRENT_LOCATION_STOP)
            .map((m) => (
              <UserMarker
                clusterer={clusterer}
                key={m.stopId}
                markerLabel={m.stopName}
                location={m.location}
                areUsersSelected={!!props?.usersSelected?.length}
                id={m.id}
              />
            ));
        }}
      </MarkerClusterer>
      {polygons.map((item) => {
        const { path, id } = item;
        return (
          <Polygon
            path={path}
            key={id}
            options={{
              fillColor: '#000',
              fillOpacity: 0.4,
              strokeColor: '#424f62',
              strokeOpacity: 1,
              strokeWeight: 2,
            }}
          />
        );
      })}
    </>
  );
}

async function getMissingParentTasks(tasks) {
  const taskIdMap = _.keyBy(tasks, 'id');
  const parentTaskIdsMissing = tasks
    .filter((t) => t.parentTaskId && !taskIdMap[t.parentTaskId])
    .map((t) => t.parentTaskId);
  if (!parentTaskIdsMissing.length) return [];
  const parentTasks = await ApiService.getTasksByTaskIds(parentTaskIdsMissing);
  return parentTasks;
}
const library = ['drawing'];
const MapViewPage = (props) => {
  const { loadError, isLoaded } = useLoadScript({
    googleMapsApiKey: props.googleMapKey,
    libraries: library,
    id: 'google-map-script',
    version: '3.47',
    // ...otherOptions
  });

  const toggleSelection = useCallback((shape) => {
    if (shape.getEditable() === true) shape.setEditable(false);
    else shape.setEditable(true);
  }, []);

  const handleOverlayComplete = useCallback((e) => {
    const shape = e.overlay;
    shape.type = e.type;
    shape.setOptions({
      clickable: true,
    });
    selected = shape;
    google.maps.event.addListener(shape, 'click', () => {
      toggleSelection(shape);
    });
    toggleSelection(shape);
    props.handleDrawingMode(null, false);
  }, []);

  const handleClick = useCallback((type) => {
    props.handleDrawingMode(type);
  }, []);

  const handleDelete = useCallback((selectedShape) => {
    if (selectedShape) {
      selectedShape.setMap(null);
    }
    props.handleDrawingMode(null, true);
  }, []);

  const handleBulkEdit = useCallback(async (selectedShape, markers) => {
    if (selectedShape) {
      const reqTasks = handleShape(selectedShape, markers);
      const parentTasks = await getMissingParentTasks(reqTasks);
      const flatTasks = TaskMapper.DbTaskToFlatTask({ tasks: [...reqTasks, ...parentTasks] });
      props.handleTaskSelection(flatTasks);
      props.handleBulkEdit();
    }
  }, []);

  if (!isLoaded) {
    return <div>Map Loading.</div>;
  }
  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>;
  }
  return (
    <>
      <div className={styles.drawingButtonGroup}>
        <Button.Group
          vertical
          style={{ position: 'absolute', top: '17%', left: '10px', background: 'white', zIndex: 1 }}
        >
          <Joyn2Button
            icon="circle outline"
            onClick={() => handleClick(null)}
            btnType={props.drawingMode === null ? 'tertiary' : 'light'}
            svg={{
              icon: pointer,
            }}
          />
          <Joyn2Button
            onClick={() => handleClick(google.maps.drawing.OverlayType.RECTANGLE)}
            disabled={!props.showShape}
            svg={{
              icon: rectangle,
            }}
            btnType={props.drawingMode === google?.maps?.drawing?.OverlayType?.RECTANGLE ? 'tertiary' : 'light'}
          />
          <Joyn2Button
            style={{ background: 'white' }}
            onClick={() => handleClick(google.maps.drawing.OverlayType.POLYGON)}
            disabled={!props.showShape}
            btnType={props.drawingMode === google?.maps?.drawing?.OverlayType.POLYGON ? 'tertiary' : 'light'}
            svg={{
              icon: polygon,
            }}
          />

          <Button
            data-tooltip="Delete shape"
            data-position="right center"
            style={{ background: 'white' }}
            icon="delete"
            onClick={() => handleDelete(selected)}
            disabled={props.showShape}
          />

          <Button
            data-tooltip="Bulk Edit"
            data-position="right center"
            style={{ background: 'white' }}
            icon="edit"
            onClick={() => handleBulkEdit(selected, props.markers)}
            disabled={props.showShape}
          />
        </Button.Group>
      </div>
      <GoogleMap
        // ref={props.onMapMounted}
        options={{
          styles: mapStyles,
          zoomControlOptions: {
            position: google.maps.ControlPosition.LEFT_BOTTOM,
          },
        }}
        mapContainerStyle={{ width: '100%', height: '100%' }}
        onLoad={props.onMapMounted}
      >
        <DrawingManager
          defaultDrawingMode={null}
          drawingMode={props.drawingMode}
          options={{
            drawingControl: false,
            drawingControlOptions: {
              position: google.maps.ControlPosition.TOP_CENTER,
              drawingModes: [
                _.get(google, 'maps.drawing.OverlayType.CIRCLE'),
                _.get(google, 'maps.drawing.OverlayType.POLYGON'),
              ],
            },
            circleOptions: {
              fillColor: `#000`,
              fillOpacity: 0.25,
              strokeWeight: 2,
              clickable: false,
              editable: true,
              zIndex: 1,
            },
          }}
          onOverlayComplete={handleOverlayComplete}
        />
        {props.showActualVsPlannedView ? <ActualVsPlannedMapView {...props} /> : <NormalMapView {...props} />}
      </GoogleMap>
    </>
  );
};

export default React.memo(WrapperMapViewPage, shouldNotUpdateComponent);
